/**
 * Device-id мост embed ↔ native shell (по образцу safe-area/push-token мостов).
 *
 * Мобильное приложение генерирует стабильный per-install `did` и хранит его
 * в secure storage; embed получает его от родителя и шлёт заголовком X-Device-Id
 * на все API-запросы. Так сессия привязывается к устройству и не зависит от
 * версии WebView/ОС.
 *
 *   embed → parent: { type: 'domcrm:get-device-id' }
 *   parent → embed: { type: 'domcrm:device-id', payload: { did } }
 *
 * Вне мобильного iframe мост неактивен (веб-клиенты привязываются по UA).
 */

const STORAGE_KEY = 'domcrm_device_id';
const PARENT_REPLY_TIMEOUT_MS = 800;
const DID_RE = /^[0-9a-fA-F-]{8,64}$/;

let currentDid: string | null = null;
let initialized = false;

export function normalizeDeviceId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (!DID_RE.test(v)) return null;
  return v;
}

export function setDeviceId(value: unknown): string | null {
  const did = normalizeDeviceId(value);
  if (!did) return null;
  currentDid = did;
  try {
    sessionStorage.setItem(STORAGE_KEY, did);
  } catch {
    /* sessionStorage может быть недоступен */
  }
  return did;
}

export function getDeviceId(): string | null {
  if (currentDid) return currentDid;
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      currentDid = normalizeDeviceId(stored);
      if (currentDid) return currentDid;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function isMobileChannelFrame(): boolean {
  try {
    if (!window.parent || window.parent === window) return false;
    return new URLSearchParams(window.location.search).get('widget_channel') === 'mobile';
  } catch {
    return false;
  }
}

/** Просит did у нативного родителя; null — не mobile-iframe или ответ не пришёл. */
export function requestParentDeviceId(timeoutMs: number = PARENT_REPLY_TIMEOUT_MS): Promise<string | null> {
  return new Promise((resolve) => {
    if (!isMobileChannelFrame()) {
      resolve(null);
      return;
    }
    let settled = false;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; payload?: { did?: unknown } } | undefined;
      if (data?.type !== 'domcrm:device-id') return;
      settled = true;
      window.removeEventListener('message', onMessage);
      resolve(setDeviceId(data.payload?.did));
    };
    window.addEventListener('message', onMessage);
    try {
      window.parent.postMessage({ type: 'domcrm:get-device-id' }, '*');
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      if (settled) return;
      window.removeEventListener('message', onMessage);
      resolve(null);
    }, timeoutMs);
  });
}

/** Дождаться did перед первым авторизованным запросом (страховка для старых shell-ов). */
export async function ensureDeviceIdForAuth(timeoutMs: number = PARENT_REPLY_TIMEOUT_MS): Promise<void> {
  if (getDeviceId()) return;
  if (!isMobileChannelFrame()) return;
  await requestParentDeviceId(timeoutMs);
}

/**
 * Инициализация: слушает проактивную отправку did от родителя и запрашивает его.
 * Идемпотентна; вызывается один раз на старте embed.
 */
export function initDeviceIdBridge(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  // Нативный shell кладёт did в URL iframe — он доступен синхронно ДО первого
  // /auth/me. Это убирает гонку: сессия уже привязана к устройству, а запрос
  // уходил без X-Device-Id и получал 401 → редирект на форму входа.
  try {
    const fromQuery = normalizeDeviceId(new URLSearchParams(window.location.search).get('did'));
    if (fromQuery) setDeviceId(fromQuery);
  } catch {
    /* ignore */
  }
  if (!isMobileChannelFrame()) return;
  window.addEventListener('message', (e) => {
    const data = e.data as { type?: string; payload?: { did?: unknown } } | undefined;
    if (data?.type === 'domcrm:device-id') {
      setDeviceId(data.payload?.did);
    }
  });
  void requestParentDeviceId().then((did) => {
    if (did) setDeviceId(did);
  });
}

/** Только для тестов. */
export function resetDeviceIdBridgeForTests(): void {
  currentDid = null;
  initialized = false;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
